import { useState } from "react";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Renders one mystical card image with a 3D flip animation.
 *
 * If the pre-generated PNG isn't on disk yet (still seeding or 404), falls
 * back to an ornate placeholder card with the card name typeset in gold.
 *
 * Props:
 *   - card: { name, reversed, slug, deck }
 *   - delay: stagger animation delay in ms
 *   - size: "sm" | "md" | "lg"
 */
export function CardImage({ card, delay = 0, size = "md" }) {
  const [imgFailed, setImgFailed] = useState(false);
  const slug = card?.slug;
  const deck = card?.deck;
  const reversed = !!card?.reversed;
  const hasImage = slug && deck && !imgFailed;

  const dims = size === "sm"
    ? { w: 80, h: 120 }
    : size === "lg"
    ? { w: 200, h: 300 }
    : { w: 130, h: 195 };

  return (
    <motion.div
      data-testid={`card-${slug || "fallback"}`}
      initial={{ rotateY: 180, opacity: 0, y: 16 }}
      animate={{ rotateY: 0, opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: delay / 1000, ease: [0.22, 1, 0.36, 1] }}
      style={{ width: dims.w, height: dims.h, perspective: 800, transformStyle: "preserve-3d" }}
      className="relative inline-block"
    >
      <div
        style={{ transform: reversed ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.4s" }}
        className="absolute inset-0 rounded-lg overflow-hidden border border-[#d4af37]/40 shadow-[0_0_20px_rgba(212,175,55,0.25)] bg-gradient-to-br from-[#1a0a1f] to-black"
      >
        {hasImage ? (
          <img
            src={`${API}/cards/${deck}/${slug}.png`}
            alt={card?.name || ""}
            onError={() => setImgFailed(true)}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-center p-2 bg-[radial-gradient(ellipse_at_center,_#2a0f3a_0%,_#0a0510_100%)]">
            <div className="absolute inset-2 border border-[#d4af37]/30 rounded" />
            <div className="text-3xl mb-1">🎴</div>
            <div className="text-[10px] uppercase tracking-widest text-[#d4af37] font-serif leading-tight">
              {card?.name || "Carta"}
            </div>
          </div>
        )}
      </div>
      {reversed && (
        <span className="absolute -top-2 -right-2 px-1.5 py-0.5 text-[8px] uppercase tracking-widest rounded bg-red-600/90 text-white border border-red-300">
          inv
        </span>
      )}
    </motion.div>
  );
}

/** Convenience wrapper for an array of cards, with staggered reveal. */
export function CardFan({ cards, size = "md" }) {
  if (!cards?.length) return null;
  return (
    <div className="flex items-center justify-center gap-3 flex-wrap py-2" data-testid="card-fan">
      {cards.map((c, i) => (
        <CardImage key={i} card={c} delay={i * 250} size={size} />
      ))}
    </div>
  );
}
